import React, { useState, useEffect } from "react";
import { Card, CardBody, Row, Col, Alert, Form } from "reactstrap";
import NumberFormat from "react-number-format";
import {
	CurrencyInput,
	IntegerInput,
} from "features/forms/components/InputElement";
import { useForm, FormProvider } from "react-hook-form";
import InputWrapper from "features/forms/components/InputWrapper";

const ExcessMileageFees = ({
	account,
	lease,
	leaseEndData,
	updateLeaseEndData,
}) => {
	const methods = useForm();

	const [currentMileage, setCurrentMileage] = useState(
		leaseEndData.excessMileage.currentMileage || 0
	);
	const [contractedMileage, setContractedMileage] = useState(
		leaseEndData.excessMileage.contractedMileage || lease.contractedMiles || 0
	);
	const [ratePerMile, setRatePerMile] = useState(
		leaseEndData.excessMileage.ratePerMile || 0.25
	);

	// Calculate excess mileage and fees
	const excessMiles = Math.max(0, currentMileage - contractedMileage);
	const totalMileageFee = excessMiles * ratePerMile;

	// Calculate payments made
	const totalPaid =
		account.principle + account.interest + (account.taxPaid || 0);
	const paymentsMade =
		account.regularPayment > 0 ? Math.floor(totalPaid / account.payment) : 0;

	// Estimate miles per month
	const monthsInLease = account.term || 1;
	const expectedMileage = (contractedMileage / monthsInLease) * monthsInLease;
	const averageMilesPerMonth = currentMileage / Math.max(1, paymentsMade);

	// Update parent component
	useEffect(() => {
		updateLeaseEndData({
			excessMileage: {
				currentMileage,
				contractedMileage,
				excessMiles,
				ratePerMile,
				totalMileageFee,
			},
		});
	}, [
		currentMileage,
		contractedMileage,
		ratePerMile,
		excessMiles,
		totalMileageFee,
	]);

	return (
		<div>
			<h4 className="mb-4">Mileage Assessment</h4>
			<p className="text-muted mb-4">
				Enter the current vehicle mileage to calculate any excess mileage fees.
			</p>

			<Row>
				<Col md="12">
					<FormProvider {...methods}>
						<Form>
							<InputWrapper
								formTitle="Mileage Information"
								inputComponents={[
									<IntegerInput
										{...methods}
										value={currentMileage}
										onChange={(val) => setCurrentMileage(parseFloat(val) || 0)}
										name="currentMileage"
										label="Current Odometer Reading *"
										placeholder="Enter current mileage"
										disableValidation
										helperText="Enter the actual odometer reading from the vehicle"
									/>,
									<IntegerInput
										{...methods}
										value={contractedMileage}
										onChange={(val) =>
											setContractedMileage(parseFloat(val) || 0)
										}
										name="contractedMileage"
										label="Contracted Mileage Limit"
										placeholder="Mileage limit per lease"
										disableValidation
										helperText="Total miles allowed under lease agreement"
									/>,
									<CurrencyInput
										{...methods}
										value={ratePerMile}
										onChange={(val) => setRatePerMile(parseFloat(val) || 0.25)}
										name="ratePerMile"
										label="Rate Per Excess Mile"
										placeholder="0.25"
										disableValidation
										helperText="Typical rates range from $0.15 to $0.35 per mile"
									/>,
								]}
							/>
						</Form>
					</FormProvider>
				</Col>

				<Col md="6">
					{/* Mileage Calculation Result */}
					<Card className="fee-summary-card">
						<CardBody>
							<h5 className="mb-3">Mileage Calculation</h5>

							<div className="fee-row">
								<span>Current Mileage:</span>
								<strong>{currentMileage.toLocaleString()} miles</strong>
							</div>

							<div className="fee-row">
								<span>Contracted Limit:</span>
								<strong>{contractedMileage.toLocaleString()} miles</strong>
							</div>

							<div className="fee-row">
								<span>Excess Miles:</span>
								<strong
									className={excessMiles > 0 ? "text-danger" : "text-success"}
								>
									{excessMiles.toLocaleString()} miles
								</strong>
							</div>

							<div className="fee-row">
								<span>Rate Per Mile:</span>
								<strong>
									<NumberFormat
										value={ratePerMile}
										displayType="text"
										prefix="$"
										decimalScale={2}
										fixedDecimalScale
									/>
								</strong>
							</div>

							<div className="fee-row">
								<span>Total Mileage Fee:</span>
								<strong
									className={
										totalMileageFee > 0 ? "text-danger" : "text-success"
									}
									style={{ fontSize: "1.3em" }}
								>
									<NumberFormat
										value={totalMileageFee}
										displayType="text"
										thousandSeparator
										prefix="$"
										decimalScale={2}
										fixedDecimalScale
									/>
								</strong>
							</div>
						</CardBody>
					</Card>

					{/* Mileage Analysis */}
					<Card className="mt-3">
						<CardBody>
							<h6>📊 Mileage Analysis</h6>

							<div className="fee-row">
								<span>Payments Made:</span>
								<strong>
									{paymentsMade} of {account.term}
								</strong>
							</div>

							<div className="fee-row">
								<span>Avg Miles Per Month:</span>
								<strong>{averageMilesPerMonth.toFixed(0)} miles/month</strong>
							</div>

							<div className="fee-row">
								<span>Expected at This Point:</span>
								<strong>
									{((contractedMileage / monthsInLease) * paymentsMade).toFixed(
										0
									)}{" "}
									miles
								</strong>
							</div>

							{excessMiles > 0 && (
								<Alert color="warning" className="mt-3 mb-0">
									<strong>⚠️ Excess Mileage Detected</strong>
									<br />
									<small>
										This vehicle has {excessMiles.toLocaleString()} excess
										miles. A fee of{" "}
										<NumberFormat
											value={totalMileageFee}
											displayType="text"
											thousandSeparator
											prefix="$"
											decimalScale={2}
											fixedDecimalScale
										/>{" "}
										will be charged.
									</small>
								</Alert>
							)}

							{excessMiles === 0 && currentMileage > 0 && (
								<Alert color="success" className="mt-3 mb-0">
									<strong>✓ Within Mileage Limit</strong>
									<br />
									<small>
										No excess mileage fees will be charged. Vehicle is within
										contracted limits.
									</small>
								</Alert>
							)}
						</CardBody>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

export default ExcessMileageFees;
